import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserSearch, Plus, ChevronRight, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ESTADO_LABELS = { abierta: "Abierta", cerrada: "Cerrada" };

export default function ContratacionLista() {
    const navigate = useNavigate();
    const [postings, setPostings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/hiring/postings");
            setPostings(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando vacantes");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><UserSearch size={22} className="text-edu-navy" /> Contratación</h1>
                <button onClick={() => setShowNew(true)} className="edu-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1.5">
                    <Plus size={14} /> Nueva vacante
                </button>
            </div>
            <p className="text-edu-muted mb-6">Describe el puesto que buscas y sube los CV que se presenten. La IA los ordena según qué tan bien encajan — tú decides.</p>

            {loading ? (
                <p className="text-sm text-edu-muted">Cargando…</p>
            ) : postings.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">Aún no hay vacantes. Crea la primera.</div>
            ) : (
                <div className="space-y-2">
                    {postings.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => navigate(`/admin/contratacion/${p.id}`)}
                            className="edu-card w-full text-left p-4 hover:bg-edu-soft transition-colors flex items-center justify-between gap-3"
                        >
                            <div>
                                <p className="font-medium text-sm">{p.titulo}</p>
                                <p className="text-xs text-edu-muted">{ESTADO_LABELS[p.estado] || p.estado}</p>
                            </div>
                            <ChevronRight size={16} className="text-edu-muted" />
                        </button>
                    ))}
                </div>
            )}

            {showNew && <NewPostingModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/admin/contratacion/${id}`)} />}
        </div>
    );
}

function NewPostingModal({ onClose, onCreated }) {
    const [titulo, setTitulo] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [saving, setSaving] = useState(false);

    async function create() {
        if (!titulo.trim() || !descripcion.trim()) {
            toast.error("Completa el título y la descripción del puesto");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/hiring/postings", { titulo: titulo.trim(), descripcion: descripcion.trim() });
            toast.success("Vacante creada");
            onCreated(data.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error creando la vacante");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-lg w-full">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Nueva vacante</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Puesto</span>
                        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="edu-input w-full" placeholder="Ej: Profesor de Ciencias — Secundaria" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Qué buscas en el puesto</span>
                        <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={6} className="edu-input w-full" placeholder="Requisitos, experiencia, formación, funciones del puesto…" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                    <button onClick={create} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm disabled:opacity-50">{saving ? "Creando…" : "Crear vacante"}</button>
                </div>
            </div>
        </div>
    );
}
